/**
 * Sprache der App und der Mails (Mails seit 16.10.2026, App seit 16.10.2026).
 * Deutsch oder Englisch, ohne Angabe Deutsch.
 *
 * WIE DIE APP ÜBERSETZT IST: jeder sichtbare Text steht an seiner Stelle in
 * beiden Sprachen, `t('Deutsch', 'English')`. Kein Schlüsselkatalog: der
 * deutsche Wortlaut bleibt, wo er war, und die Tests, die ihn prüfen, gelten
 * weiter. `t` liest die aktuelle Sprache beim Aufruf, also beim Rendern.
 * Texte auf Modulebene (Konstanten) dürfen `t` deshalb nicht beim Laden
 * aufrufen, sonst friert die Sprache ein: dort eine Funktion oder beide
 * Fassungen ablegen und beim Gebrauch wählen.
 *
 * WOHER DIE SPRACHE KOMMT, in dieser Reihenfolge:
 *   1. `?lang=en` oder `?lang=de` in der Adresse (die englische Website hängt
 *      `lang=en` an ihre Links zur Registrierung), gemerkt im localStorage;
 *   2. die gemerkte Wahl (Umschalter DE/EN, `waehleSprache`);
 *   3. der Browser: Deutsch nur, wenn er Deutsch zuerst nennt.
 * Nach der Anmeldung gleicht `SpracheAbgleich` (session.tsx) mit
 * `accounts.sprache` ab: die Spalte steuert die Mails (Server, sprache.ts).
 *
 * WECHSEL: `waehleSprache` setzt die Sprache, merkt sie und baut die App neu
 * auf (App.tsx hängt die Sprache als key an die Wurzel). Ein Wechsel ist
 * selten; neu aufbauen ist einfacher als jede Seite auf Änderungen hören zu
 * lassen.
 */

import { useSyncExternalStore } from 'react';

export type Sprache = 'de' | 'en';

const MERKER = '99takes-sprache';

export function spracheAusText(v: string | null | undefined): Sprache | null {
  const t = (v ?? '').trim().toLowerCase();
  if (/^de\b/.test(t)) return 'de';
  if (/^en\b/.test(t)) return 'en';
  return null;
}

/** Die Sprache des Browsers: Deutsch nur, wenn er Deutsch zuerst nennt. Ohne Browser Deutsch. */
export function browserSprache(nav: { languages?: readonly string[]; language?: string } | undefined =
  typeof navigator === 'undefined' ? undefined : navigator): Sprache {
  const erste = nav?.languages?.[0] ?? nav?.language ?? '';
  return spracheAusText(erste) ?? (erste ? 'en' : 'de');
}

function lies(): Sprache | null {
  try { return spracheAusText(localStorage.getItem(MERKER)); } catch { return null; }
}

function merke(s: Sprache) {
  try { localStorage.setItem(MERKER, s); } catch { /* privates Fenster: gilt bis zum Neuladen */ }
}

/** Gibt es eine gemerkte Wahl auf diesem Gerät (Umschalter oder ?lang=)? */
export const hatGemerkteSprache = (): boolean => lies() !== null;

/** `?lang=` aus der Adresse merken. Liefert die Sprache, wenn eine dastand. */
export function merkeSpracheAusAdresse(suche: string = typeof location === 'undefined' ? '' : location.search): Sprache | null {
  const s = spracheAusText(new URLSearchParams(suche).get('lang'));
  if (s) merke(s);
  return s;
}

const ausAdresse: Sprache | null = typeof window === 'undefined' ? null : merkeSpracheAusAdresse();
/** Stand `?lang=` beim Laden in der Adresse? Dann gewinnt es auch gegen das Konto. */
export const spracheAusAdresse = (): Sprache | null => ausAdresse;

let aktuell: Sprache = typeof window === 'undefined' ? 'de' : (ausAdresse ?? lies() ?? browserSprache());
const hoerer = new Set<() => void>();

function setzeDokument(s: Sprache) {
  if (typeof document !== 'undefined') document.documentElement.lang = s;
}
setzeDokument(aktuell);

/** Die Sprache, in der gerade gerendert wird. */
export const sprache = (): Sprache => aktuell;

/** Der Text in der aktuellen Sprache. Auch für JSX: `t(<>…</>, <>…</>)`. */
export function t<T>(de: T, en: T): T {
  return aktuell === 'en' ? en : de;
}

/** Für Intl und toLocale…: 'de-DE' oder 'en-GB' (Tag vor Monat, 24 Stunden, wie die Mails). */
export const locale = (s: Sprache = aktuell): string => (s === 'en' ? 'en-GB' : 'de-DE');

/**
 * Sprache setzen. `merken: false` für Tests und den Abgleich mit dem Konto,
 * wenn nichts gemerkt werden soll.
 */
export function setzeSprache(s: Sprache, { merken = true }: { merken?: boolean } = {}) {
  if (merken) merke(s);
  if (s === aktuell) return;
  aktuell = s;
  setzeDokument(s);
  for (const h of hoerer) h();
}

/** Für die Wurzel der App: rendert neu, wenn die Sprache wechselt. */
export function useSprache(): Sprache {
  return useSyncExternalStore(
    (h) => { hoerer.add(h); return () => { hoerer.delete(h); }; },
    () => aktuell,
    () => aktuell,
  );
}

/** Die Sprache für die Mails dieses Besuchs: dieselbe wie die der App. */
export function mailSprache(): Sprache {
  return aktuell;
}
